import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  AdminPanelUserActionRequest,
  AdminPanelUserDetails,
} from '../../models/admin-panel-user-details';
import { AdminPanelUserCorrespondenceEntry } from '../../models/admin-panel-user-correspondence-entry';
import { AdminPanelUserHistoryEntry } from '../../models/admin-panel-user-history-entry';
import { AdminPanelUserLoginHistoryEntry } from '../../models/admin-panel-user-login-history-entry';
import { AdminPanelUserOrganization } from '../../models/admin-panel-user-organization';
import { OrganizationRefreshResponse } from '../../models/organization-refresh-response';
import { TableRequestData } from '../../models/table/table-request-param';
import { TableResponse } from '../../models/table-response';
import { environment } from '../../../environments/environment';
import { HttpUtils } from '../../utils/http.utils';

@Injectable()
export class AdminPanelUsersApi {
  #httpClient: HttpClient = inject(HttpClient);
  readonly #apiUrl = environment.API_EWNIOSKI + '/admin/uzytkownicy';

  getUserLoginHistory$(
    userDetails: AdminPanelUserDetails,
    _params: TableRequestData,
  ): Observable<TableResponse<AdminPanelUserLoginHistoryEntry>> {
    const params = this.#buildUserIdentityParams(userDetails, _params);

    return this.#httpClient.get<TableResponse<AdminPanelUserLoginHistoryEntry>>(
      `${this.#apiUrl}/historia-logowania`,
      {
        params: HttpUtils.toHttpParams(params),
        withCredentials: true,
      },
    );
  }

  getUserEventsHistory$(
    userDetails: AdminPanelUserDetails,
    _params: TableRequestData,
  ): Observable<TableResponse<AdminPanelUserHistoryEntry>> {
    const params = this.#buildUserIdentityParams(userDetails, _params);

    return this.#httpClient.get<TableResponse<AdminPanelUserHistoryEntry>>(
      `${this.#apiUrl}/historia-zdarzen`,
      {
        params: HttpUtils.toHttpParams(params),
        withCredentials: true,
      },
    );
  }

  getUserOrganizations$(
    userDetails: AdminPanelUserDetails,
    _params: TableRequestData,
  ): Observable<TableResponse<AdminPanelUserOrganization>> {
    const params = this.#buildUserOrganizationIdentityParams(
      userDetails,
      _params,
    );

    return this.#httpClient.get<TableResponse<AdminPanelUserOrganization>>(
      `${this.#apiUrl}/organizacje`,
      {
        params: HttpUtils.toHttpParams(params),
        withCredentials: true,
      },
    );
  }

  getUserCorrespondence$(
    userDetails: AdminPanelUserDetails,
    _params: TableRequestData,
  ): Observable<TableResponse<AdminPanelUserCorrespondenceEntry>> {
    return this.#httpClient.get<TableResponse<AdminPanelUserCorrespondenceEntry>>(
      `${this.#apiUrl}/korespondencja`,
      {
        params: HttpUtils.toHttpParams(
          this.#buildUserIdentityParams(userDetails, _params),
        ),
        withCredentials: true,
      },
    );
  }

  toggleUserAccountLock$(
    userId: number,
    payload: AdminPanelUserActionRequest,
  ): Observable<AdminPanelUserDetails> {
    return this.#httpClient.post<AdminPanelUserDetails>(
      `${this.#apiUrl}/${userId}/lock`,
      payload,
      {
        withCredentials: true,
      },
    );
  }

  toggleUserSystemAdminRole$(
    userId: number,
    payload: AdminPanelUserActionRequest,
  ): Observable<AdminPanelUserDetails> {
    return this.#httpClient.post<AdminPanelUserDetails>(
      `${this.#apiUrl}/${userId}/admin`,
      payload,
      {
        withCredentials: true,
      },
    );
  }

  refreshOrganizationData$(
    organizationId: number,
  ): Observable<OrganizationRefreshResponse> {
    return this.#httpClient.post<OrganizationRefreshResponse>(
      `${this.#apiUrl}/organizacje/${organizationId}/refresh`,
      {},
      {
        withCredentials: true,
      },
    );
  }

  #buildUserIdentityParams(
    userDetails: AdminPanelUserDetails,
    params: TableRequestData,
  ): TableRequestData & {
    firstName: string;
    lastName: string;
    pesel: string;
  } {
    return {
      ...params,
      firstName: userDetails.personalData.firstName,
      lastName: userDetails.personalData.lastName,
      pesel: userDetails.personalData.pesel,
    };
  }

  #buildUserOrganizationIdentityParams(
    userDetails: AdminPanelUserDetails,
    params: TableRequestData,
  ): TableRequestData & {
    userFirstName: string;
    userLastName: string;
    userPesel: string;
  } {
    return {
      ...params,
      userFirstName: userDetails.personalData.firstName,
      userLastName: userDetails.personalData.lastName,
      userPesel: userDetails.personalData.pesel,
    };
  }
}
